import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  LogOut,
  Menu,
  Users,
  X,
  Plane,
  Anchor,
  Settings,
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  UserCircle2,
  LayoutGrid,
  Handshake,
  Sliders,
  Receipt,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext.jsx';
import logoIcon from '../images/optycargo-icon.png';

// Sub-ítems de "Parametrizaciones". Si más adelante hay más
// parametrizaciones, se agregan acá.
const PARAMETRIZACIONES = [
  { to: '/users', label: 'Usuarios', icon: Users },
  { to: '/airports', label: 'Aeropuertos', icon: Plane },
  { to: '/seaports', label: 'Puertos', icon: Anchor },
  { to: '/incoterms', label: 'Incoterms', icon: FileText },
  { to: '/commodity-types', label: 'Tipos de Mercancía', icon: Package },
  { to: '/parametrizacion/conceptos', label: 'Conceptos de Cotización', icon: Receipt },
  { to: '/document-types', label: 'Tipos de Documento', icon: FileText },
  { to: '/parametrizacion/proveedores', label: 'Proveedores', icon: Handshake },
  { to: '/general-settings', label: 'Configuración General', icon: Sliders },
];

const Navbar = () => {
  const { user, tenant, isPlatformMode, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isParamOpen, setIsParamOpen] = useState(false);
  const location = useLocation();

  const activeLinkClass = 'bg-primary/20 text-primary';
  const inactiveLinkClass = 'hover:bg-primary/10 hover:text-primary/80';

  const initials = (user?.nombre || user?.name || user?.email || '?')
    .toString()
    .charAt(0)
    .toUpperCase();

  const roleLabel = user?.rol || user?.role || '';

  // El trigger "Parametrizaciones" se ve activo si la ruta actual
  // es una sub-ruta de parametrización.
  const isParamActive = PARAMETRIZACIONES.some((p) =>
    location.pathname === p.to || location.pathname.startsWith(p.to + '/'),
  );

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    setIsParamOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="bg-card shadow-lg sticky top-0 z-50"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Logo + contexto */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logoIcon}
              alt="OptyCargo"
              className="h-10 w-10 object-contain"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-semibold text-foreground text-lg">OptyCargo</span>
              <span className="text-xs text-muted-foreground">
                {isPlatformMode ? 'Plataforma matriz' : tenant?.nombre || tenant?.slug || 'Operador'}
              </span>
            </div>
            <span className="sm:hidden font-semibold text-foreground text-lg">OptyCargo</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2 flex-1 justify-end">
            {isPlatformMode && (
              <NavLink
                to="/tenants"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${
                    isActive ? activeLinkClass : inactiveLinkClass
                  }`
                }
              >
                <Building2 className="h-5 w-5" />
                <span>Clientes Operadores</span>
              </NavLink>
            )}

            {/* Clientes: módulo de dominio (solo operadores) */}
            {!isPlatformMode && (
              <NavLink
                to="/clients"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${
                    isActive ? activeLinkClass : inactiveLinkClass
                  }`
                }
              >
                <UserCircle2 className="h-5 w-5" />
                <span>Clientes</span>
              </NavLink>
            )}

            {/* Módulos — punto de entrada a los módulos del ERP (solo tenants) */}
            {!isPlatformMode && (
              <NavLink
                to="/modulos"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${
                    isActive ? activeLinkClass : inactiveLinkClass
                  }`
                }
              >
                <LayoutGrid className="h-5 w-5" />
                <span>Módulos</span>
              </NavLink>
            )}

            {/* Dropdown Parametrizaciones — siempre al final, pegado al avatar */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${
                    isParamActive ? activeLinkClass : inactiveLinkClass
                  }`}
                >
                  <Settings className="h-5 w-5" />
                  <span>Parametrizaciones</span>
                  <ChevronDown className="h-4 w-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                  Administración
                </DropdownMenuLabel>
                {PARAMETRIZACIONES.map((p) => {
                  const Icon = p.icon;
                  return (
                    <DropdownMenuItem key={p.to} asChild>
                      <NavLink
                        to={p.to}
                        className={({ isActive }) =>
                          `w-full flex items-center space-x-2 ${
                            isActive ? 'bg-primary/15 text-primary' : ''
                          }`
                        }
                      >
                        <Icon className="h-4 w-4" />
                        <span>{p.label}</span>
                      </NavLink>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="ml-2 rounded-full w-10 h-10 p-0"
                    aria-label="Menú de usuario"
                  >
                    <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {initials}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5 text-sm">
                    <div className="text-foreground font-medium truncate">
                      {user?.nombre || user?.name || user?.email}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user?.correo || user?.email}
                    </div>
                    {roleLabel && (
                      <div className="mt-1">
                        <Badge variant="secondary" className="font-normal">
                          {roleLabel}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <ThemeToggle variant="buttons" />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center text-red-600 hover:text-red-700"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Cerrar sesión</span>
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle variant="select" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen((v) => !v)}
              aria-label="Alternar menú"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-card shadow-lg rounded-b-lg p-4 space-y-2 border-t border-border max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          {isPlatformMode && (
            <MobileNavLink
              to="/tenants"
              icon={<Building2 className="h-5 w-5" />}
              label="Clientes Operadores"
              onClick={() => setIsOpen(false)}
            />
          )}

          {!isPlatformMode && (
            <MobileNavLink
              to="/clients"
              icon={<UserCircle2 className="h-5 w-5" />}
              label="Clientes"
              onClick={() => setIsOpen(false)}
            />
          )}

          {/* Módulos — entry point a los módulos del ERP (solo tenants) */}
          {!isPlatformMode && (
            <MobileNavLink
              to="/modulos"
              icon={<LayoutGrid className="h-5 w-5" />}
              label="Módulos"
              onClick={() => setIsOpen(false)}
            />
          )}

          {/* Solicitudes Aéreas — top-level en mobile, antes del grupo Parametrizaciones */}

          {/* Grupo Parametrizaciones en mobile: header expandible con sub-items — siempre al final, pegado al bloque de usuario */}
          <div>
            <button
              type="button"
              onClick={() => setIsParamOpen((v) => !v)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-md text-base font-medium transition-colors duration-300 ${
                isParamActive ? 'bg-primary/20 text-primary' : 'hover:bg-primary/10 hover:text-primary/80'
              }`}
            >
              <span className="flex items-center space-x-3">
                <Settings className="h-5 w-5" />
                <span>Parametrizaciones</span>
              </span>
              {isParamOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {isParamOpen && (
              <div className="mt-1 ml-4 space-y-1 border-l-2 border-border pl-2">
                {PARAMETRIZACIONES.map((p) => {
                  const Icon = p.icon;
                  return (
                    <MobileNavLink
                      key={p.to}
                      to={p.to}
                      icon={<Icon className="h-5 w-5" />}
                      label={p.label}
                      onClick={() => setIsOpen(false)}
                      subItem
                    />
                  );
                })}
              </div>
            )}
          </div>

          {user && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="px-4 py-2 text-sm font-medium text-foreground truncate">
                {user?.nombre || user?.name || user?.email}
              </div>
              <div className="px-4 text-xs text-muted-foreground mb-3 truncate">
                {user?.correo || user?.email}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
    </motion.nav>
  );
};

function MobileNavLink({ to, icon, label, onClick, subItem = false }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `block ${
          subItem ? 'px-4 py-2' : 'px-4 py-3'
        } rounded-md text-base font-medium transition-colors duration-300 flex items-center space-x-3 ${
          isActive ? 'bg-primary/20 text-primary' : 'hover:bg-primary/10 hover:text-primary/80'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default Navbar;
